import { z } from 'zod';
import { Channel, Confidence, Id, JsonObject, LanguageTag, Timestamp } from './common';

/**
 * Universal event model.
 *
 * Every inbound or outbound thing that happens on any channel is normalised to an Event.
 * The `events` table is append-only: edits, deletions, deliveries and reads are themselves
 * new events that reference the original via `reply_to` / `raw_payload`.
 */
export const EventType = z.enum([
  'text',
  'audio',
  'image',
  'document',
  'video',
  'link',
  'location',
  'contact',
  'reaction',
  'edit',
  'delete',
  'delivery',
  'read',
  'external',
]);
export type EventType = z.infer<typeof EventType>;

export const EventDirection = z.enum(['inbound', 'outbound', 'internal']);
export type EventDirection = z.infer<typeof EventDirection>;

export const Extraction = z.object({
  kind: z.enum(['transcript', 'ocr', 'caption', 'summary', 'entities', 'table', 'other']),
  content: z.string(),
  language: LanguageTag.optional(),
  confidence: Confidence,
  model: z.string().optional(),
  created_at: Timestamp,
});
export type Extraction = z.infer<typeof Extraction>;

/** What an attachment is to the product (drives limits, steps and rendering). */
export const AttachmentKind = z.enum(['audio', 'image', 'document', 'link']);
export type AttachmentKind = z.infer<typeof AttachmentKind>;

export const Attachment = z.object({
  attachment_id: Id,
  person_id: Id.optional(),
  event_id: Id.optional(),
  kind: AttachmentKind,
  /** Lowercase hex SHA-256 of the raw bytes (or of the URL for links). */
  content_hash: z.string().regex(/^[a-f0-9]{64}$/),
  mime: z.string().min(1),
  size_bytes: z.number().int().nonnegative(),
  filename: z.string().optional(),
  /** Private object key (`person_id/attachment_id`); served only through GET /media/:id. */
  r2_key: z.string().optional(),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
  duration_ms: z.number().int().nonnegative().optional(),
  pages: z.number().int().positive().optional(),
  /** Voice notes: the transcript the turn ran on. */
  transcript: z.string().optional(),
  /** Images / documents: structured JSON or text extracted by the model; links: page text. */
  extracted_text: z.string().optional(),
  /** Links: the fetched URL and its page title. */
  url: z.string().optional(),
  title: z.string().optional(),
  extractions: z.array(Extraction).default([]),
  created_at: Timestamp,
});
export type Attachment = z.infer<typeof Attachment>;

export const Quote = z.object({
  event_id: Id.optional(),
  channel_message_id: z.string().optional(),
  text: z.string().optional(),
  author: z.string().optional(),
});
export type Quote = z.infer<typeof Quote>;

export const ForwardedFrom = z.object({
  channel: Channel,
  sender_name: z.string().optional(),
  sender_id: z.string().optional(),
  original_date: Timestamp.optional(),
});
export type ForwardedFrom = z.infer<typeof ForwardedFrom>;

export const Location = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  accuracy_m: z.number().nonnegative().optional(),
  label: z.string().optional(),
});
export type Location = z.infer<typeof Location>;

export const ContactCard = z.object({
  name: z.string(),
  phones: z.array(z.string()).default([]),
  emails: z.array(z.string()).default([]),
  vcard: z.string().optional(),
});
export type ContactCard = z.infer<typeof ContactCard>;

export const Event = z.object({
  event_id: Id,
  person_id: Id,
  channel_identity_id: Id,
  conversation_id: Id,
  thread_id: Id.optional(),
  type: EventType,
  direction: EventDirection,
  /** Message id on the originating channel (Telegram message_id, WhatsApp wamid, web client id). */
  channel_message_id: z.string().optional(),
  reply_to: Id.optional(),
  forwarded_from: ForwardedFrom.optional(),
  quote: Quote.optional(),
  text: z.string().optional(),
  location: Location.optional(),
  contact: ContactCard.optional(),
  reaction: z.string().optional(),
  attachments: z.array(Attachment).default([]),
  language_tags: z.array(LanguageTag).default([]),
  /** Verbatim payload from the channel, for audit and re-processing. */
  raw_payload: JsonObject.optional(),
  /** Correlates the event with model_calls and gateway logs for one turn. */
  trace_id: Id.optional(),
  created_at: Timestamp,
});
export type Event = z.infer<typeof Event>;

/** Input shape used when a channel adapter hands a new event to the core (ids are assigned later). */
export const NewEvent = Event.omit({ event_id: true, created_at: true }).extend({
  created_at: Timestamp.optional(),
});
export type NewEvent = z.infer<typeof NewEvent>;

/** One line of the "recent items" list the prompt carries so references resolve. */
export interface RecentItem {
  ordinal: number;
  kind: AttachmentKind;
  title: string;
  attachment_id: string;
  event_id?: string | undefined;
}

/**
 * The last `max` attachments and links of a conversation, oldest first, numbered from 1, so
 * "the second one" / "الثاني" and "the receipt" / "الفاتورة" can be resolved by the model.
 */
export function recentItems(events: readonly Event[], max = 10): RecentItem[] {
  const all: Omit<RecentItem, 'ordinal'>[] = [];
  for (const ev of events) {
    for (const a of ev.attachments) {
      all.push({
        kind: a.kind,
        title: itemTitle(a),
        attachment_id: a.attachment_id,
        event_id: ev.event_id,
      });
    }
  }
  return all.slice(-max).map((item, i) => ({ ordinal: i + 1, ...item }));
}

/** Short human title for an attachment (for the prompt list and the UI). */
export function itemTitle(a: Attachment): string {
  if (a.kind === 'link') return a.title?.trim() || a.url || 'link';
  if (a.kind === 'audio') {
    const t = a.transcript?.trim();
    return t ? `voice note: ${t.slice(0, 60)}${t.length > 60 ? '…' : ''}` : 'voice note';
  }
  if (a.kind === 'document') return a.filename || 'document';
  const extracted = extractedKind(a);
  return extracted ? `${extracted}${a.filename ? ` (${a.filename})` : ''}` : a.filename || 'image';
}

/** The `kind` field of a structured extraction ("receipt", "ticket"...), when present. */
export function extractedKind(a: Attachment): string | undefined {
  if (!a.extracted_text) return undefined;
  try {
    const parsed = JSON.parse(a.extracted_text) as { kind?: unknown };
    return typeof parsed.kind === 'string' ? parsed.kind : undefined;
  } catch {
    return undefined;
  }
}
